// Ruleset contract (docs/11-features/play-modes-and-rulesets.md).
//
// A match is `{ seed, ruleset, seats }`. The ruleset (modules + tuning +
// content rating) is per match; assists and AI profiles are per seat. This
// file is the JSON contract between the setup screen (client), the adapter
// (`AdapterOptions.ruleset` / `.seats`) and the sim's Rust mirror
// (`packages/sim/src/rules/`, roadmap R1). Keep it additive: ids are
// persisted in saves, share codes and replays.

// ── Modules (spec §4) ──────────────────────────────────────────────────

export const MODULE_IDS = [
  'economy.upkeep',
  'economy.starvation',
  'economy.supply',
  'economy.roads',
  'economy.trade_routes',
  'doctrine.enabled',
  'heroes.enabled',
  'heroes.loyalty',
  'combat.formations',
  'combat.siege',
  'combat.naval',
  'sea.trade',
  'faith.enabled',
  'faith.crises',
  'magic.enabled',
  'magic.rituals',
  'science.research',
  'diplomacy.basic',
  'diplomacy.full',
  'espionage.enabled',
  'world.neutrals',
  'world.ruins',
  'world.fog_of_war',
  'mature.slavery',
  'mature.sacrifice',
  'pacing.truce',
  'pacing.intensity',
  'victory.conquest',
  'victory.economic',
  'victory.science',
  'victory.faith',
  'victory.diplomatic',
  'victory.milestones',
  'terrain.levels',
  'legends.enabled',
  'diplomacy.dynasty',
  'map.custom',
  'heroes.captivity',
  'heroes.quests',
  'espionage.ops',
  'espionage.false_flag',
  'diplomacy.generations',
  'espionage.counter_intel',
] as const;

export type ModuleId = (typeof MODULE_IDS)[number];

/** `core` (cities, units, movement, combat, food & gold) is always on and not listed. */
export const MODULE_DEPENDS: Readonly<Partial<Record<ModuleId, readonly ModuleId[]>>> = {
  'economy.trade_routes': ['economy.roads'],
  'heroes.loyalty': ['heroes.enabled'],
  'faith.crises': ['faith.enabled'],
  'magic.rituals': ['magic.enabled'],
  'diplomacy.full': ['diplomacy.basic'],
  'mature.sacrifice': ['faith.enabled'],
  'victory.science': ['science.research'],
  'victory.faith': ['faith.enabled'],
  'victory.diplomatic': ['diplomacy.full'],
  'legends.enabled': ['doctrine.enabled'],
  'sea.trade': ['combat.naval', 'economy.trade_routes'],
  'diplomacy.dynasty': ['diplomacy.full'],
  'heroes.captivity': ['heroes.enabled'],
  'heroes.quests': ['heroes.enabled'],
  'espionage.ops': ['espionage.enabled'],
  'espionage.false_flag': ['espionage.ops'],
  'diplomacy.generations': ['diplomacy.dynasty'],
  'espionage.counter_intel': ['espionage.enabled'],
};

export type ModuleCategory =
  | 'economy'
  | 'doctrine'
  | 'heroes'
  | 'combat'
  | 'faith'
  | 'magic'
  | 'science'
  | 'diplomacy'
  | 'espionage'
  | 'world'
  | 'map'
  | 'mature'
  | 'pacing'
  | 'victory'
  | 'sea';

export function moduleCategory(id: ModuleId): ModuleCategory {
  return id.split('.')[0] as ModuleCategory;
}

export const VICTORY_MODULES: readonly ModuleId[] = MODULE_IDS.filter((m) =>
  m.startsWith('victory.'),
);

// ── Tuning (spec §5) ───────────────────────────────────────────────────

export interface Tuning {
  readonly pace: 'relaxed' | 'normal' | 'brisk';
  readonly mapSize: 'tiny' | 'small' | 'medium' | 'large';
  /** AI opponents, 1–7. */
  readonly aiOpponents: number;
  readonly startingResources: 'low' | 'normal' | 'high';
  readonly victoryLength: 'short' | 'normal' | 'long';
  /** Minutes before any AI may declare war on / attack a human, 0–20. */
  readonly peacePeriodMin: number;
  /** When false, a human's last city can't be taken for good early on (refuge). */
  readonly cityCanBeDestroyed: boolean;
  /** Truce length after a decisive event, 0–5 minutes (pacing.truce). */
  readonly truceMin: number;
  /** AI pressure ceiling before it eases off (pacing.intensity). */
  readonly intensityCeiling: 'low' | 'medium' | 'high' | 'off';
}

// ── Content rating (spec §6) ───────────────────────────────────────────

export type ContentRating = 'family' | 'teen' | 'mature';

// ── Per-seat assists and AI profiles (spec §8–9) ───────────────────────

export interface Assists {
  readonly handicap: 'none' | 'easy' | 'very_easy';
  readonly autoManageCities: 'off' | 'suggest' | 'auto';
  readonly hints: 'off' | 'smart' | 'always';
  readonly revealMap: boolean;
  readonly pauseOnDanger: 'off' | 'major' | 'all';
  readonly uiMode: 'icons' | 'standard' | 'expert';
  readonly speedCap: 1 | 2 | 4;
}

export type AiProfile = 'gentle' | 'easy' | 'normal' | 'hard' | 'brutal';

/** A human seat's alignment choice. Absent = unaligned (cities start unspecialized). */
export type SeatAlignment = 'light' | 'dark';

export type Seat =
  | { readonly kind: 'human'; readonly assists: Assists; readonly alignment?: SeatAlignment }
  | { readonly kind: 'ai'; readonly ai: AiProfile };

// ── Ruleset ────────────────────────────────────────────────────────────

export type PresetId =
  | 'explorer'
  | 'apprentice'
  | 'standard'
  | 'strategist'
  | 'grand'
  | 'highlands'
  | 'legends'
  | 'seafarers'
  | 'royal-houses'
  | 'custom';

// Climate / Thirst / the sea axis live in ./axes.ts (400-line rule); imported
// for this file and re-exported so the contract surface is unchanged.
import type { Climate, Sea, SeaFrame, SeaState, Thirst } from './axes.js';
export type { Climate, Sea, SeaFrame, SeaState, Thirst };
export { RIM_SEA } from './axes.js';

export interface Ruleset {
  /** Contract version; bump only with a migration. */
  readonly v: 1;
  /** The preset it came from; 'custom' once anything differs. */
  readonly preset: PresetId;
  /** Enabled modules, sorted, closed under MODULE_DEPENDS. */
  readonly modules: readonly ModuleId[];
  readonly tuning: Tuning;
  readonly content: ContentRating;
  /** Absent = 'temperate' (older share codes decode to it). */
  readonly climate?: Climate;
  /** Absent = 'auto' (older share codes decode to it). */
  readonly thirst?: Thirst;
  /** Absent = 'rim' (older share codes decode to it). */
  readonly sea?: Sea;
  /**
   * The calendar axis (generations design §4a): real minutes per in-game
   * year, 2–12 in half-minute steps. Absent = 6 (the classic pace, ~3600
   * ticks a year). Fixed for a match — chosen at setup, never changed
   * mid-match. Travels in the full rules only, never the share code (it
   * needs 5 bits; an `AOD3` layout extension is the owner's call).
   */
  readonly yearMinutes?: number;
}

export interface MatchSetup {
  readonly ruleset: Ruleset;
  /** Seat 0 is the local human in single-player. */
  readonly seats: readonly Seat[];
  /** An authored `aod-map` document (JSON) to play on; absent = procedural. */
  readonly map?: string;
}

// ── Validation ─────────────────────────────────────────────────────────

/** Enable `id` and everything it depends on (transitively). */
export function withDependencies(mods: Iterable<ModuleId>): Set<ModuleId> {
  const out = new Set<ModuleId>();
  const add = (m: ModuleId) => {
    if (out.has(m)) return;
    out.add(m);
    for (const d of MODULE_DEPENDS[m] ?? []) add(d);
  };
  for (const m of mods) add(m);
  return out;
}

/** Modules that (transitively) depend on `id` — turned off with it. */
export function dependents(id: ModuleId): ModuleId[] {
  return MODULE_IDS.filter((m) => m !== id && withDependencies([m]).has(id));
}

/** Content rating forces mature modules off. */
export function applyContentRating(mods: Set<ModuleId>, content: ContentRating): Set<ModuleId> {
  if (content === 'family') {
    for (const m of ['mature.slavery', 'mature.sacrifice'] as const) mods.delete(m);
  }
  return mods;
}

/** Problems with a ruleset, in plain words for the setup screen; [] when valid. */
export function validateRuleset(r: Ruleset): string[] {
  const problems: string[] = [];
  const set = new Set(r.modules);
  for (const m of r.modules) {
    if (!(MODULE_IDS as readonly string[]).includes(m)) problems.push(`Unknown module: ${m}`);
    for (const d of MODULE_DEPENDS[m] ?? []) {
      if (!set.has(d)) problems.push(`${m} needs ${d}`);
    }
  }
  if (!VICTORY_MODULES.some((v) => set.has(v))) problems.push('At least one way to win is needed');
  if (r.content === 'family' && (set.has('mature.slavery') || set.has('mature.sacrifice'))) {
    problems.push('Family content rating excludes mature themes');
  }
  const t = r.tuning;
  if (t.aiOpponents < 1 || t.aiOpponents > 7) problems.push('AI opponents must be 1–7');
  if (t.peacePeriodMin < 0 || t.peacePeriodMin > 20) problems.push('Peace period must be 0–20 min');
  if (t.truceMin < 0 || t.truceMin > 5) problems.push('Truce must be 0–5 min');
  if (r.yearMinutes !== undefined) {
    if (r.yearMinutes < 2 || r.yearMinutes > 12) problems.push('Year length must be 2–12 min');
    else if ((r.yearMinutes * 2) % 1 !== 0)
      problems.push('Year length must be in half-minute steps');
  }
  return problems;
}

// ── Module presets (spec §10) ────────────────────────────────────────

// terrain.levels and legends.enabled ship dark (docs/terrain-levels-design.md,
// docs/champion-system-design.md): no MODULE_IDS-derived preset arms them —
// only explicit presets opt in (Highlands, Legends; Seafarers for sea.trade;
// Royal Houses for diplomacy.dynasty, docs/dynasty-design.md). map.custom is
// armed by nothing: it comes on only when a match is played through
// `setup_from_map` on an authored map (docs/aod-studio-design.md §3.4).
// heroes.captivity is dark for the same reason (docs/aod-studio-design.md
// §4.5 (b)): a hero who loses a fight is killed as today until the owner
// arms the module — so no default board and no determinism pin moves.
// heroes.quests is dark too (docs/hero-quests-design.md): a hero takes no
// orders until the owner arms the module.
// espionage.ops, espionage.false_flag and espionage.counter_intel are dark too
// (docs/espionage-ops-design.md §5): no player sabotage order until the owner
// arms them, and no preset does.
// diplomacy.generations is dark too (docs/generations-design.md): the courts
// carry their born_tick, but no child is born, nobody ages a rule and no
// line succeeds until the owner arms it.
export const DARK_MODULES: readonly ModuleId[] = [
  'terrain.levels',
  'legends.enabled',
  'sea.trade',
  'diplomacy.dynasty',
  'map.custom',
  'heroes.captivity',
  'heroes.quests',
  'espionage.ops',
  'espionage.false_flag',
  'diplomacy.generations',
  'espionage.counter_intel',
];

/**
 * Modules sorted by their position in `MODULE_IDS`, closed under their
 * dependencies — the canonical order every ruleset's `modules` list uses.
 */
export const sortedModules = (mods: Iterable<ModuleId>): ModuleId[] =>
  [...withDependencies(mods)].sort((a, b) => MODULE_IDS.indexOf(a) - MODULE_IDS.indexOf(b));

export const baseTuning: Tuning = {
  pace: 'normal',
  mapSize: 'medium',
  aiOpponents: 3,
  startingResources: 'normal',
  victoryLength: 'normal',
  peacePeriodMin: 0,
  cityCanBeDestroyed: true,
  truceMin: 1.5,
  intensityCeiling: 'high',
};

export const baseAssists: Assists = {
  handicap: 'none',
  autoManageCities: 'off',
  hints: 'smart',
  revealMap: false,
  pauseOnDanger: 'major',
  uiMode: 'standard',
  speedCap: 4,
};

/**
 * Rust's `Tuning::default()` — "neutral: changes nothing when applied".
 * Distinct from `baseTuning` above, which is the *contract's* neutral
 * tuning (three AI opponents, a 1.5-minute truce, high pressure). The
 * sim's own default is what a match plays under when the caller says
 * nothing, so the mirror of Rust's default has to be exact.
 */
const rustDefaultTuning: Tuning = {
  pace: 'normal',
  mapSize: 'medium',
  aiOpponents: 1,
  startingResources: 'normal',
  victoryLength: 'normal',
  peacePeriodMin: 0,
  cityCanBeDestroyed: true,
  truceMin: 0,
  intensityCeiling: 'off',
};

/** The classic calendar (generations design §4a): 6 real minutes per
 * in-game year — 3600 ticks. Absent `yearMinutes` means this. */
export const DEFAULT_YEAR_MINUTES = 6;

/**
 * The default ruleset — the mirror of Rust's `Ruleset::default()`
 * (`packages/sim/src/rules/mod.rs`): every module on except
 * `victory.milestones` and the dark ones, the sim's neutral tuning,
 * mature content, temperate climate, automatic thirst, the rim sea.
 *
 * A match plays under this when nothing else says otherwise — which is
 * what an authored map with no `ruleset` of its own gets.
 */
export function defaultRuleset(): Ruleset {
  return {
    v: 1,
    preset: 'custom',
    modules: sortedModules(
      MODULE_IDS.filter((m) => m !== 'victory.milestones' && !DARK_MODULES.includes(m)),
    ),
    tuning: rustDefaultTuning,
    content: 'mature',
    climate: 'temperate',
    thirst: 'auto',
    sea: 'rim',
    yearMinutes: DEFAULT_YEAR_MINUTES,
  };
}

/**
 * The neutral assists — the mirror of Rust's `Assists::default()`
 * (`packages/sim/src/rules/seats.rs`). A human seat with no stated
 * assists gets these.
 */
export function defaultAssists(): Assists {
  return { ...baseAssists };
}
